import { ReservationStatusLabel } from '@idc-forms/types/reservation'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { HistoryIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import { Avatar, AvatarFallback } from '@/shared/ui/avatar'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { useReservationHistory } from '../../hooks/use-inventory-query'
import { type IListReservationHistory, ReservationStatus } from '../../models/inventory'
import { ReservationHistoryEvent } from './reservation-history-event'

const RESERVATION_STATUS_COLORS: Record<ReservationStatus, string> = {
  [ReservationStatus.ACTIVE]: 'bg-blue-100 text-blue-700 dark:bg-blue-700/30 dark:text-blue-200',
  [ReservationStatus.PENDING_AUTHORIZATION]:
    'bg-amber-100 text-amber-700 dark:bg-amber-700/30 dark:text-amber-200',
  [ReservationStatus.EXPIRED]: 'bg-gray-100 text-gray-600 dark:bg-gray-600/30 dark:text-gray-300',
  [ReservationStatus.RELEASED]: 'bg-gray-100 text-gray-600 dark:bg-gray-600/30 dark:text-gray-300',
  [ReservationStatus.DECLINED]: 'bg-red-100 text-red-700 dark:bg-red-700/30 dark:text-red-200'
}

function formatDateRange(reservation: IListReservationHistory): string {
  const start = format(parseISO(reservation.createdAt), 'd MMM yyyy', { locale: es })
  if (reservation.reservedDates.length > 0) {
    const lastDate = reservation.reservedDates[reservation.reservedDates.length - 1]
    const end = format(parseISO(lastDate), 'd MMM yyyy', { locale: es })
    return `${start} — ${end}`
  }
  return start
}

interface Props {
  propertyId: string
}

const ReservationHistory = ({ propertyId }: Props) => {
  const { data: reservations } = useReservationHistory(propertyId)

  if (!reservations || reservations.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="default">
            <HistoryIcon className="h-16 w-16" />
          </EmptyMedia>
          <EmptyTitle>Sin historial de actividades</EmptyTitle>
          <EmptyDescription>
            El historial de apartados aparecerá aquí una vez que se registren acciones sobre este
            inmueble.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <Accordion type="multiple" defaultValue={[reservations[0]._id]} className="w-full">
      {reservations.map((reservation) => (
        <AccordionItem key={reservation._id} value={reservation._id}>
          <AccordionTrigger className="px-3 hover:no-underline">
            <div className="flex flex-col items-start gap-1 text-left">
              <div className="flex items-center gap-2">
                <Avatar seed={reservation.reservedBy?.fullname} className="h-5 w-5">
                  <AvatarFallback className="text-[10px]">
                    {reservation.reservedBy?.fullname?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{reservation.reservedBy?.fullname}</span>
                <span
                  className={cn(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded',
                    RESERVATION_STATUS_COLORS[reservation.status]
                  )}
                >
                  {ReservationStatusLabel[reservation.status]}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">{formatDateRange(reservation)}</span>
              {reservation.authorizedBy && (
                <span className="text-xs text-muted-foreground">
                  Autorizado por{' '}
                  <span className="font-medium text-foreground">
                    {reservation.authorizedBy.fullname}
                  </span>
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3 pt-2">
            <ReservationNotes reservation={reservation} />
            <div className="flex flex-col">
              {reservation.history.map((event, idx) => (
                <ReservationHistoryEvent
                  key={`${reservation._id}-${idx}`}
                  event={event}
                  isLast={idx === reservation.history.length - 1}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

function ReservationNotes({ reservation }: { reservation: IListReservationHistory }) {
  const notes = [
    reservation.notes && { label: 'Nota', text: reservation.notes },
    reservation.authorizationNotes && {
      label: 'Nota de autorización',
      text: reservation.authorizationNotes
    },
    reservation.releaseNotes && { label: 'Nota de liberación', text: reservation.releaseNotes }
  ].filter(Boolean) as Array<{ label: string; text: string }>

  if (notes.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5 mb-3">
      {notes.map((note) => (
        <div key={note.label} className="bg-muted/50 rounded-md p-2">
          <p className="text-[10px] font-semibold text-muted-foreground mb-0.5">{note.label}</p>
          <p className="text-xs text-foreground">{note.text}</p>
        </div>
      ))}
    </div>
  )
}

export default ReservationHistory
